import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { unpackLabelFromResponseData } from 'helpers/formatHelper';
import {
    BudgetBys,
    BudgetExpenseOrIncome,
    BudgetPhases,
    BudgetSections,
} from 'business/constants';
import { transformQueryFilter } from 'helpers/queryHelpers';
import { UseQueryResult } from '@tanstack/react-query';
import { snakifyFiltersFields } from 'routes/BudgetModule/components/shared/Filters/helpers/filtersHelpers';
import { getDataPostProcessingMiddleware } from 'routes/BudgetModule/shared/getDataMiddlewares';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import {
    InscriptionQueryKey,
    createBudgetQueryKeys,
} from '../createBudgetQueryKeys';
import { useInscriptionQuery } from './useCustomQuery';
import { snakify } from 'helpers/syntaxHelper';

type InscriptionsQueryParams = {
    budgetBy: BudgetBys;
    expenseOrIncome?: BudgetExpenseOrIncome;
    investmentOperating?: BudgetSections;
    exercice?: number;
    operationSoftwareId?: string;
    secondaryBudgetAxisElementSoftwareId?: string;
    enrichedOperationSoftwareId?: string;
    chapterSoftwareId?: string;
    budgetProposalIds?: number[];
    budgetName?: string;
    phase?: BudgetPhases;
    resourceSoftwareId?: string;
    additionalFilters?: QueryFilterT[];
    enabled?: boolean;
};

type InscriptionsQueryResultWithCacheKey = UseQueryResult<
    { data: InscriptionT[]; columns: ResponseColumnT[] },
    HttpError
> & { cacheKey: InscriptionQueryKey };

// TODO: Consider refactoring to use orgChartFinance.tree.label or orgChartFinance.name to avoid this unpacking.
/**
 * Applies post-processing to the provided inscriptions data :
 * Unpack the objects fields that contain a value field (e.g : orgChartFinance.tree) : agGrid colDef requirements.
 */
const postProcessInscriptionsData = ({
    inscriptionsData,
}: {
    inscriptionsData: InscriptionT[];
}): InscriptionT[] => {
    // @ts-expect-error This is necessary because unpackLabelFromResponseData does not match the expected type
    return inscriptionsData.map(unpackLabelFromResponseData);
};

/**
 * A custom hook to fetch inscriptions data based on the provided filters and parameters.
 *
 * @param budgetBy - Specifies how the budget is organized (e.g., by department, by resource).
 * @param expenseOrIncome - Filter by expense or income type (optional).
 * @param investmentOperating - Filter by section : operating or investment (optional).
 * @param additionalFilters - Array of filters to refine the query results (optional, defaults to []).
 * @param exercice - Specifies the exercise (optional).
 * @param operationSoftwareId - Operation software identifier (optional).
 * @param enrichedOperationSoftwareId - Enriched operation software identifier (optional).
 * @param budgetProposalId - Budget proposal ID (optional).
 * @param budgetName - Budget name (optional).
 * @param phase - The current phase of the budget. It is used to avoid getting inscriptions that doesn't exist in that phase (optional).
 * @param resourceSoftwareId - Resource software identifier (optional).
 *
 * @returns The fetched inscriptions data and the cache key.
 */
const useGetInscriptions = ({
    budgetBy,
    expenseOrIncome,
    investmentOperating,
    exercice,
    operationSoftwareId,
    secondaryBudgetAxisElementSoftwareId,
    enrichedOperationSoftwareId,
    chapterSoftwareId,
    budgetProposalIds,
    budgetName,
    phase,
    resourceSoftwareId,
    additionalFilters = [],
    enabled = true,
}: InscriptionsQueryParams): InscriptionsQueryResultWithCacheKey => {
    const { inscriptionsKeys } = createBudgetQueryKeys();

    const cacheKey = inscriptionsKeys({
        expenseOrIncome,
        investmentOperating,
        additionalFilters,
        exercice,
        operationSoftwareId,
        enrichedOperationSoftwareId,
        secondaryBudgetAxisElementSoftwareId,
        chapterSoftwareId,
        budgetName,
        budgetProposalIds,
        resourceSoftwareId,
    });

    const query: UseQueryResult<
        { data: InscriptionT[]; columns: ResponseColumnT[] },
        HttpError
    > = useInscriptionQuery(
        cacheKey,
        async () => {
            const response = await RQAPICallWithData<{
                data: InscriptionT[];
                columns: ResponseColumnT[];
            }>({
                endpoint: 'inscriptions',
                method: 'POST',
                body: {
                    expenseIncome: expenseOrIncome,
                    investmentOperating: investmentOperating,
                    budgetBy: snakify(budgetBy),
                    filters: transformQueryFilter({
                        queryFilters: snakifyFiltersFields(additionalFilters),
                    }),
                    operationSoftwareId,
                    enrichedOperationSoftwareId,
                    secondaryBudgetAxisElementSoftwareId,
                    exercice,
                    phase,
                    budgetName,
                    budgetProposalIds,
                    resourceSoftwareId,
                },
                postProcessingCallback: getDataPostProcessingMiddleware,
            });

            return {
                ...response.content.data,
                data: postProcessInscriptionsData({
                    inscriptionsData: response.content.data.data,
                }),
            };
        },
        { enabled, refetchOnWindowFocus: false },
    );

    return { ...query, cacheKey };
};

/**
 * Fetches all inscriptions based on budgetBy, expenseOrIncome, exercice, budgetName, and phase.
 */
export const useGetAllInscriptions = (
    params: Pick<
        InscriptionsQueryParams,
        'budgetBy' | 'expenseOrIncome' | 'exercice' | 'budgetName' | 'phase'
    >,
): InscriptionsQueryResultWithCacheKey => useGetInscriptions(params);

/**
 * Fetches inscription data for budget analysis.
 */
export const useGetAnalyseInscriptions = (
    params: Pick<
        InscriptionsQueryParams,
        'budgetBy' | 'expenseOrIncome' | 'phase' | 'additionalFilters'
    >,
): InscriptionsQueryResultWithCacheKey => useGetInscriptions(params);

/**
 * Fetches inscriptions for a given budget proposal.
 */
export const useGetBudgetProposalsInscriptions = (
    params: Pick<
        InscriptionsQueryParams,
        | 'budgetBy'
        | 'budgetProposalIds'
        | 'additionalFilters'
        | 'enabled'
        | 'phase'
    >,
): InscriptionsQueryResultWithCacheKey => useGetInscriptions(params);

/**
 * Fetches inscriptions for a given budget proposal and resources.
 */
export const useGetResourceInscriptions = (
    params: Pick<
        InscriptionsQueryParams,
        | 'budgetBy'
        | 'budgetProposalIds'
        | 'phase'
        | 'expenseOrIncome'
        | 'resourceSoftwareId'
        | 'enabled'
    >,
): InscriptionsQueryResultWithCacheKey => useGetInscriptions(params);

/**
 * Fetches operation-related inscriptions.
 */
export const useGetOperationInscriptions = (
    params: Pick<
        InscriptionsQueryParams,
        | 'budgetBy'
        | 'phase'
        | 'expenseOrIncome'
        | 'exercice'
        | 'enrichedOperationSoftwareId'
        | 'secondaryBudgetAxisElementSoftwareId'
        | 'operationSoftwareId'
    >,
): InscriptionsQueryResultWithCacheKey => useGetInscriptions(params);

/**
 * Fetches inscriptions with operation data (AllOperations view).
 */
export const useGetInscriptionsWithOperationData = (
    params: Pick<
        InscriptionsQueryParams,
        'budgetBy' | 'expenseOrIncome' | 'exercice'
    >,
): InscriptionsQueryResultWithCacheKey => useGetInscriptions(params);
